<template>
  <SectionPageShell
    section="media"
    :title="mediaTitle"
    :subtitle="file ? 'Medien bearbeiten' : null"
    show-back-link
  >
    <div v-if="file" class="row media-edit-wrapper">
        <div class="col-12 col-lg-8 asset-wrapper my-3">
          <div class="row py-3">
            <div class="col-12">
              <h4>Details</h4>
            </div>
          </div>
          <template v-if="file.content_type === 'application/pdf'">
            <div class="pdf-wrapper position-relative">
              <VuePdfEmbed :page="1" :source="file.auto_url" />
            </div>
          </template>
          <template v-else>
            <div class="d-flex flex-column justify-content-center">
              <img :alt="file.alt_text" class="img-fluid" :src="file.auto_url" />
            </div>
          </template>
        </div>
        <div class="col-12 col-lg-4 settings-wrapper p-4">
          <Form
            @submit="updateMedia"
            v-slot="{ errors }"
            :initial-values="{ asset: file }"
            :validation-schema="schema"
            class="row"
          >
            <div class="col-12 mt-3">
              <div class="form-floating">
                <Field
                  id="asset.alt_text"
                  name="asset.alt_text"
                  class="form-control"
                  :class="{ 'is-invalid': errors['asset.alt_text'] }"
                />
                <label for="asset.alt_text">Bildbeschreibung (alt Text)</label>
                <div v-if="errors['asset.alt_text']" class="invalid-feedback">
                  {{ errors["asset.alt_text"] }}
                </div>
              </div>
            </div>
            <div class="col-12 mt-3">
              <div class="form-floating">
                <Field
                  id="asset.caption"
                  name="asset.caption"
                  class="form-control"
                  :class="{ 'is-invalid': errors['asset.caption'] }"
                />
                <label for="asset.caption">Caption</label>
                <div v-if="errors['asset.caption']" class="invalid-feedback">
                  {{ errors["asset.caption"] }}
                </div>
              </div>
            </div>
            <div class="col-12 mt-3">
              <div class="form-floating">
                <Field
                  id="asset.copyright"
                  name="asset.copyright"
                  class="form-control"
                  :class="{ 'is-invalid': errors['asset.copyright'] }"
                />
                <label for="asset.copyright">Copyright</label>
                <div v-if="errors['asset.copyright']" class="invalid-feedback">
                  {{ errors["asset.copyright"] }}
                </div>
              </div>
            </div>
            <div class="col-12 mt-3">
              <div class="row">
                <div class="col-12">
                  Kategorien:
                  <template v-if="mediaCategories.length === 0">keine</template>
                </div>
                <div
                  class="col-12 col-lg-4"
                  :key="category.id"
                  v-for="category in mediaCategories"
                >
                  <div class="form-check">
                    <Field
                      type="checkbox"
                      :value="category.id"
                      name="asset.asset_category_ids"
                      v-slot="{ field }"
                    >
                      <input
                        type="checkbox"
                        :value="category.id"
                        v-bind="field"
                        name="asset.asset_category_ids"
                        :id="`asset.asset_categories${category.id}`"
                        class="form-check-input"
                      />
                      <label
                        class="form-check-label"
                        :for="`asset.asset_categories${category.id}`"
                      >
                        {{ category.name }}
                      </label>
                    </Field>
                  </div>
                </div>
                <div class="col-12">
                  <a href="#" @click.prevent="addMediaCategoryOpen = true"
                    >+ Neue Kategorie zum Filtern anlegen</a
                  >
                  <Dialog
                    v-model:visible="addMediaCategoryOpen"
                    modal
                    header="Kategorie hinzufügen"
                  >
                    <input
                      v-model="addMediaCategoryName"
                      type="text"
                      class="form-control my-2"
                      placeholder="Name"
                    />
                    <button
                      class="btn btn-success"
                      type="button"
                      @click.prevent="addMediaCategory"
                    >
                      Speichern
                    </button>
                  </Dialog>
                </div>
              </div>
            </div>
            <div class="col-12 mt-3">
              <button type="submit" class="btn btn-success">
                Änderungen speichern
              </button>
            </div>
            <div class="asset-info my-3">
              <br />
              <b>Datei-Typ:</b> {{ file.content_type }}
              <br />
              <b>Dateigröße:</b> {{ formatBytes(file.file_size) }}
            </div>
          </Form>
        </div>
    </div>
  </SectionPageShell>
</template>

<script>
import SectionPageShell from "@/components/base_type/SectionPageShell.vue";
import { useUserStore } from "@/stores/user";
import { useMediaStore } from "@/stores/media";
import { useMediaCategoryStore } from "@/stores/media_category";
import { Helpers } from "@/helpers";
import { Form, Field } from "vee-validate";
import Yup from "@/yup";
import Dialog from "primevue/dialog";
import VuePdfEmbed from "vue-pdf-embed";
export default {
  name: "MediaEdit",
  components: {
    SectionPageShell,
    VuePdfEmbed,
    Form,
    Field,
    Dialog,
  },
  setup() {
    return {
      mediaStore: useMediaStore(),
      userStore: useUserStore(),
      mediaCategoryStore: useMediaCategoryStore(),
    };
  },
  data() {
    return {
      file: null,
      addMediaCategoryOpen: false,
      addMediaCategoryName: "",
      mediaCategories: [],
      schema: Yup.object().shape({
        asset: Yup.object().shape({
          alt_text: Yup.string().required(),
        }),
      }),
    };
  },
  created() {
    this.mediaStore
      .get(this.$route.params.asset_id, this.userStore.user.organisation.kdnr)
      .then(({ data }) => {
        this.file = data;
      });
    this.mediaCategoryStore
      .index(this.userStore.user.organisation.kdnr)
      .then(({ data }) => {
        this.mediaCategories = data;
      });
  },
  methods: {
    updateMedia(values, { resetForm }) {
      this.mediaStore
        .update(
          this.$route.params.asset_id,
          this.userStore.user.organisation.kdnr,
          values
        )
        .then(({ data }) => {
          this.file = data.asset || data;
          resetForm({ values: { asset: this.file } });
          this.mediaStore.assets = this.mediaStore.assets.map((asset) => (
            asset.id === this.file.id ? {...asset, ...this.file} : asset
          ));
          Helpers.emitter.emit('success', 'Medienangaben gespeichert.');
        })
        .catch((error) => {
          if (error.response?.status === 409) {
            Helpers.emitter.emit('error', 'Das Medium wurde zwischenzeitlich geändert. Bitte laden Sie die Seite neu.');
          } else {
            Helpers.emitter.emit('error', error.response?.data?.message || error.message || 'Medienangaben konnten nicht gespeichert werden.');
          }
        });
    },
    addMediaCategory() {
      this.mediaCategoryStore
        .create(
          this.userStore.user.organisation.kdnr,
          this.addMediaCategoryName
        )
        .then(({ data }) => {
          this.mediaCategories.push(data);
          this.addMediaCategoryOpen = false;
        });
    },
  },
  computed: {
    mediaTitle() {
      if (!this.file) {
        return 'Medien bearbeiten'
      }

      return this.file.alt_text || this.file.caption || `Medien #${this.file.id}`
    },
  },
};
</script>
<style scoped lang="scss">
@import "src/scss/constants";
.media-edit-wrapper {
  border: 1px solid #707070;
  a {
    text-decoration: none;
    color: $manet_blue_links;
    font-weight: bold;
  }
  .pdf-wrapper {
    border: 1px solid #707070;
  }
  .settings-wrapper {
    background: #e5e5e5 0 0 no-repeat padding-box;
  }
  .asset-info {
    border-top: 1px solid #707070;
  }
}
</style>
